/**
 * The company's abbreviation for artificial intelligence: "AI" (default) or "UI" (umetna
 * inteligenca), profile field `ai_term`. With "UI" every standalone "AI" in text the company and
 * its staff see becomes "UI"; with "AI" nothing changes.
 *
 * Never changed: the product name "AI semafor" in all its forms, the law name "AI Act" (and its
 * forms), URLs and e-mail addresses, the names passed in `names` (catalog tools, register
 * entries, the company), and text protected with protect() (what a person typed).
 *
 * This file is shared by the server (netlify/lib/term.ts) and the web app
 * (app/frontend/src/i18n/term.ts). The two copies must stay identical (netlify/tests/term.test.ts).
 * No imports: pure functions only.
 */

export type AiTerm = "AI" | "UI";
export const AI_TERMS = ["AI", "UI"] as const;
export const DEFAULT_TERM: AiTerm = "AI";

/** A stored or sent value as a term ("UI" or else the default "AI"). */
export function asTerm(value: unknown): AiTerm {
  return value === "UI" ? "UI" : "AI";
}

/** A standalone "AI": not part of a longer word ("OpenAI", "AIX"); "AI-ja" counts. */
const TOKEN = /(?<![\p{L}\p{N}_])AI(?![\p{L}\p{N}_])/gu;

// A protected "AI" is written as two private-use characters (one for "A", one for "I"): the same
// length, no letter case, not a letter, so string operations (trim, slice, case changes, sentence
// splits) treat company data as before. restore() turns them back into "A" and "I".
const SENT_A = "";
const SENT_I = "";
const SENTINEL = /[]/;
const SENTINELS = /[]/g;

/** True when the text has a standalone "AI". */
export function hasAi(text: string): boolean {
  if (!text.includes("AI")) return false;
  TOKEN.lastIndex = 0;
  const found = TOKEN.test(text);
  TOKEN.lastIndex = 0;
  return found;
}

/** Text a person typed (or any interpolated value): its "AI" stays "AI" whatever the term. */
export function protect(text: string): string {
  return text.includes("AI") ? text.replace(TOKEN, SENT_A + SENT_I) : text;
}

/** Protected "AI" back to "AI" (a cut half back to its own letter). */
export function restore(text: string): string {
  return SENTINEL.test(text) ? text.replace(SENTINELS, (c) => (c === SENT_A ? "A" : "I")) : text;
}

/** Fixed phrases that never change: the product name, the law name, URLs, e-mail addresses. */
const FIXED: RegExp[] = [
  /AI[\s\u00a0-]+(?:[Ss]emafor|SEMAFOR)\p{L}*/gu,
  /AI[\s\u00a0]+Act\p{L}*/gu,
  /(?:https?:\/\/|www\.)[^\s<>"'«»)\]]+/giu,
  /[\p{L}\p{N}._%+-]+@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)*\.[a-z]{2,}/giu,
];

/** Well-known product names with a standalone "AI" (the catalog adds its own names). */
export const PRODUCT_NAMES: readonly string[] = [
  "Notion AI",
  "Zoom AI Companion",
  "AI Companion",
  "AI Builder",
  "Azure AI",
  "Vertex AI",
  "Google AI Studio",
  "Meta AI",
  "Mistral AI",
  "Perplexity AI",
  "Slack AI",
  "Zendesk AI",
  "Pipedrive AI",
  "Freddy AI",
  "Read AI",
];

/**
 * Names worth protecting: they contain a standalone "AI" and are more than the bare token
 * (a register entry named just "AI" must not keep every "AI" unchanged).
 */
export function protectedNames(names: Iterable<string | null | undefined>): string[] {
  const out = new Set<string>();
  for (const raw of names) {
    const name = (raw ?? "").trim();
    if (name.length > 2 && hasAi(name)) out.add(name);
  }
  // longest first, so "Zoom AI Companion" wins over "AI Companion"
  return [...out].sort((a, b) => b.length - a.length);
}

/**
 * The text with every standalone "AI" written as `term`, except in fixed phrases, in `names`
 * and in protected text. With "AI" the text comes back as it is (protected parts restored).
 */
export function termText(text: string, term: AiTerm, names: readonly string[] = []): string {
  if (term === "AI" || !text.includes("AI")) return restore(text);
  const keep: [number, number][] = [];
  for (const re of FIXED) {
    re.lastIndex = 0;
    for (const m of text.matchAll(re)) keep.push([m.index, m.index + m[0].length]);
  }
  for (const name of names) {
    if (!name) continue;
    for (let i = text.indexOf(name); i >= 0; i = text.indexOf(name, i + 1)) keep.push([i, i + name.length]);
  }
  const out = text.replace(TOKEN, (m: string, offset: number) => (keep.some(([a, b]) => offset >= a && offset < b) ? m : term));
  return restore(out);
}

/** A text transform for one company: term + protected names; null when nothing changes. */
export type Tx = (text: string) => string;

export function termTx(term: AiTerm, names: readonly string[] = []): Tx | null {
  if (term === "AI") return null;
  const list = protectedNames([...PRODUCT_NAMES, ...names]);
  return (text: string) => termText(text, term, list);
}

// --------------------------------------------------------------------------
// Payloads: copies with the term applied to the text fields the app wrote (never to what a
// person typed). Shapes are structural, so the server and the web app share these.
// --------------------------------------------------------------------------

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);

/** A string, or every string of a list, through tx; anything else as it is. */
function txValue(v: unknown, tx: Tx): unknown {
  if (typeof v === "string") return tx(v);
  if (Array.isArray(v)) return v.map((x) => (typeof x === "string" ? tx(x) : x));
  return v;
}

/** A copy of obj with tx on the named fields (strings or lists of strings). */
export function termFields<T>(obj: T, tx: Tx, fields: readonly string[]): T {
  if (!isObj(obj)) return obj;
  const out: Obj = { ...obj };
  for (const f of fields) if (f in out) out[f] = txValue(out[f], tx);
  return out as T;
}

function termList<T>(list: unknown, tx: Tx, each: (item: T, tx: Tx) => T): unknown {
  return Array.isArray(list) ? list.map((x) => each(x as T, tx)) : list;
}

/** Traffic-light result: headline, explanation, duties, steps, open questions. */
export function termTriage<T>(triage: T, tx: Tx | null): T {
  if (!tx || !isObj(triage)) return triage;
  const out = termFields(triage as Obj, tx, ["headline", "reasoning", "open_questions"]);
  out.obligations = termList<Obj>(out.obligations, tx, (o) => termFields(o, tx, ["title", "why", "how"]));
  out.actions = termList<Obj>(out.actions, tx, (a) => termFields(a, tx, ["text"]));
  return out as T;
}

/** A register entry: only its traffic-light result (the rest is company data). */
export function termSystem<T>(system: T, tx: Tx | null): T {
  if (!tx || !isObj(system) || !isObj(system.triage)) return system;
  return { ...system, triage: termTriage(system.triage, tx) } as T;
}

export function termSystems<T>(systems: T[], tx: Tx | null): T[] {
  return tx ? systems.map((s) => termSystem(s, tx)) : systems;
}

/** Overview: the guide steps and the deadlines. */
export function termOverview<T>(ov: T, tx: Tx | null): T {
  if (!tx || !isObj(ov)) return ov;
  const step = (s: Obj) => termFields(s, tx, ["label", "hint", "time", "cta"]);
  const out: Obj = { ...ov };
  out.steps = termList<Obj>(out.steps, tx, step);
  if (isObj(out.next_step)) out.next_step = step(out.next_step);
  out.deadlines = termList<Obj>(out.deadlines, tx, (d) => termFields(d, tx, ["title", "detail"]));
  return out as T;
}

/**
 * A catalog entry: category, descriptions and hints, also in its account variants (the name, vendor,
 * aliases and plan names are product names).
 */
export function termCatalogEntry<T>(entry: T, tx: Tx | null): T {
  if (!tx) return entry;
  const out = termFields(entry, tx, ["category", "description", "ai_features", "typical_uses", "watch_out", "admin_discovery"]);
  if (!isObj(out) || !isObj(out.accounts)) return out;
  const accounts: Obj = { ...out.accounts };
  for (const key of ["personal", "business"]) {
    if (isObj(accounts[key])) accounts[key] = termFields(accounts[key] as Obj, tx, ["watch_out", "admin_discovery"]);
  }
  return { ...(out as Obj), accounts } as T;
}

export function termCatalog<T>(entries: T[], tx: Tx | null): T[] {
  return tx ? entries.map((e) => termCatalogEntry(e, tx)) : entries;
}

/** Rules summary: disclaimer, deadlines and duty titles. */
export function termRules<T>(rules: T, tx: Tx | null): T {
  if (!tx || !isObj(rules)) return rules;
  const out = termFields(rules as Obj, tx, ["disclaimer"]);
  out.deadlines = termList<Obj>(out.deadlines, tx, (d) => termFields(d, tx, ["title", "detail"]));
  out.obligations = termList<Obj>(out.obligations, tx, (o) => termFields(o, tx, ["title", "summary"]));
  out.supervisors = termList<Obj>(out.supervisors, tx, (s) => termFields(s, tx, ["scope"]));
  return out as T;
}

/** Clarifying questions of an agent check. */
export function termQuestions<T>(questions: T[], tx: Tx | null): T[] {
  if (!tx) return questions;
  return questions.map((q) => {
    const out = termFields(q as Obj, tx, ["text", "help"]);
    out.options = termList<Obj>(out.options, tx, (o) => termFields(o, tx, ["label"]));
    return out as T;
  });
}

/** One literacy module (with or without the answer key). */
export function termModule<T>(module: T, tx: Tx | null): T {
  if (!tx || !isObj(module)) return module;
  const out = termFields(module as Obj, tx, ["title", "objectives", "content_md"]);
  out.quiz = termList<Obj>(out.quiz, tx, (q) => termFields(q, tx, ["question", "options", "explanation"]));
  return out as T;
}

/** Literacy plan: role labels and modules (role ids, module ids and tool names stay). */
export function termPlan<T>(plan: T, tx: Tx | null): T {
  if (!tx || !isObj(plan)) return plan;
  const out: Obj = { ...plan };
  out.roles = termList<Obj>(out.roles, tx, (r) => termFields(r, tx, ["label", "who", "refresh"]));
  out.modules = termList<Obj>(out.modules, tx, (m) => termModule(m, tx));
  return out as T;
}

/** Survey analysis: summary, findings, ideas, and what the proposals say (not their names). */
export function termAnalysis<T>(analysis: T, tx: Tx | null): T {
  if (!tx || !isObj(analysis)) return analysis;
  const out = termFields(analysis as Obj, tx, ["summary"]);
  out.findings = termList<Obj>(out.findings, tx, (f) => termFields(f, tx, ["title", "detail"]));
  out.ideas = termList<Obj>(out.ideas, tx, (i) => termFields(i, tx, ["title", "detail"]));
  out.proposed_systems = termList<Obj>(out.proposed_systems, tx, (p) => termFields(p, tx, ["purpose", "concern"]));
  return out as T;
}

/** One chat event (text deltas stay as streamed: the model writes the term itself). */
export function termChatEvent<T>(event: T, tx: Tx | null): T {
  if (!tx || !isObj(event)) return event;
  switch (event.type) {
    case "systems":
      return { ...event, systems: Array.isArray(event.systems) ? termSystems(event.systems, tx) : event.systems } as T;
    case "suggestions":
      return termFields(event, tx, ["options"]);
    case "catalog_picker":
      return { ...event, entries: Array.isArray(event.entries) ? termCatalog(event.entries, tx) : event.entries } as T;
    case "tool":
      return termFields(event, tx, ["label"]);
    case "error":
      return termFields(event, tx, ["message"]);
    default:
      return event;
  }
}

/** Chat history: the assistant's messages and their events, the last suggestions and picker. */
export function termChatHistory<T>(history: T, tx: Tx | null): T {
  if (!tx || !isObj(history)) return history;
  const out: Obj = { ...history };
  out.messages = termList<Obj>(out.messages, tx, (m) => {
    const msg: Obj = m.role === "assistant" ? termFields(m, tx, ["text"]) : { ...m };
    msg.events = termList<Obj>(msg.events, tx, (e) => termChatEvent(e, tx));
    return msg;
  });
  out.suggestions = txValue(out.suggestions, tx);
  if (isObj(out.catalog_picker)) {
    const p = out.catalog_picker;
    out.catalog_picker = { ...p, entries: Array.isArray(p.entries) ? termCatalog(p.entries, tx) : p.entries };
  }
  return out as T;
}
